import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import axios from "axios";
import * as S from "./styles";
import Toolbar from "./Toolbar";
import ItemSelectionPanel from "./ItemSelectionPanel";
import DateSelectionPanel from "./DateSelectionPanel";
import TableView from "./TableView";
import CalendarView from "./CalendarView";
import { Box, CircularProgress } from "@mui/material";
import PLAN_URL from "../../../utils/PLAN_URL";

dayjs.locale("ko");

function ResultGrid() {
  const { group_code, project_code } = useParams();
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsData, setItemsData] = useState([]);
  const [stockData, setStockData] = useState({});
  const [orderData, setOrderData] = useState([]);
  const [chartInstance, setChartInstance] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState("table");
  const getInitialDateRange = () => {
    try {
      const storageKey = `resultGrid_dateRange_${group_code}_${project_code}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          start: dayjs(parsed.start),
          end: dayjs(parsed.end),
        };
      }
    } catch (error) {
      console.error("Error loading date range from localStorage:", error);
    }
    return {
      start: dayjs(),
      end: dayjs().add(14, "day"),
    };
  };
  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [presetPeriod, setPresetPeriod] = useState("custom");
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [isItemPanelOpen, setIsItemPanelOpen] = useState(false);
  const [isDatePanelOpen, setIsDatePanelOpen] = useState(false);
  const [isPanelPinned, setIsPanelPinned] = useState(false);
  const calendarRef = useRef(null);
  const mainContentRef = useRef(null);
  useEffect(() => {
    try {
      const storageKey = `resultGrid_dateRange_${group_code}_${project_code}`;
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          start: dateRange.start.format("YYYY-MM-DD"),
          end: dateRange.end.format("YYYY-MM-DD"),
        })
      );
    } catch (error) {
      console.error("Error saving date range to localStorage:", error);
    }
  }, [dateRange, group_code, project_code]);
  const fetchOrderData = useCallback(async () => {
    try {
      setDataLoading(true);
      const start_date = dateRange.start.format("YYYY-MM-DD");
      const end_date = dateRange.end.format("YYYY-MM-DD");
      const response = await axios.get(
        PLAN_URL +
          `/api/plan/getOrderByDate?group_code=${group_code}&project_code=${project_code}&start_date=${start_date}&end_date=${end_date}`
      );
      if (response.data && Array.isArray(response.data)) {
        setOrderData(response.data);
      } else {
        setOrderData([]);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      setOrderData([]);
    } finally {
      setDataLoading(false);
    }
  }, [group_code, project_code, dateRange]);
  const fetchResultData = useCallback(async () => {
    try {
      setDataLoading(true);
      const response = await axios.get(
        PLAN_URL +
          `/api/plan/getResultData?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        const itemsMap = {};
        const categoriesMap = {};
        const planData = response.data.filter((item) => item.type === "plan");
        planData.forEach((item) => {
          if (!itemsMap[item.item_id]) {
            itemsMap[item.item_id] = {
              id: item.item_id,
              name: item.item_name,
              code: item.item_code,
              materialGroup: item.material_grouping || "기타",
              safetyStock: item.safety_stock || 0,
              color: item.gantt_color || "#000000",
              productionPlan: {},
            };
          }
          if (!itemsMap[item.item_id].productionPlan[item.prod_date]) {
            itemsMap[item.item_id].productionPlan[item.prod_date] = 0;
          }
          itemsMap[item.item_id].productionPlan[item.prod_date] += item.qty;
          const category = item.material_grouping || "기타";
          if (!categoriesMap[category]) {
            categoriesMap[category] = [];
          }
          if (!categoriesMap[category].includes(item.item_id)) {
            categoriesMap[category].push(item.item_id);
          }
        });
        const stockIncomingData = response.data.filter(
          (item) => item.type === "stock"
        );
        window.incomingStockData = stockIncomingData;
        const initialExpandedCategories = {};
        Object.keys(categoriesMap).forEach((category, index) => {
          initialExpandedCategories[category] = index === 0;
        });
        setExpandedCategories(initialExpandedCategories);
        const itemsArray = Object.values(itemsMap);
        setItemsData(itemsArray);
        if (itemsArray.length > 0) {
          setSelectedItems([itemsArray[0].id.toString()]);
        }
        await fetchStockData();
        await fetchOrderData();
      }
    } catch (error) {
      console.error("Error fetching result data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [group_code, project_code, fetchOrderData]);
  const fetchStockData = useCallback(async () => {
    try {
      const response = await axios.get(
        PLAN_URL +
          `/api/stock/getSummary?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data) {
        const stockMap = {};
        response.data.forEach((item) => {
          stockMap[item.item_code] = Number(item.total_stock_quantity);
        });
        setStockData(stockMap);
      }
    } catch (error) {
      console.error("Error fetching stock data:", error);
    }
  }, [group_code, project_code]);
  const itemCategories = useMemo(() => {
    const categories = {};
    itemsData.forEach((item) => {
      const category = item.materialGroup || "기타";
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(item.id.toString());
    });
    return categories;
  }, [itemsData]);
  const displayDates = useMemo(() => {
    const dates = [];
    let currentDate = dateRange.start.clone();
    const endDate = dateRange.end;
    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, "day")
    ) {
      dates.push(currentDate.format("YYYY-MM-DD"));
      currentDate = currentDate.add(1, "day");
    }
    return dates;
  }, [dateRange]);
  const chartDateLabels = useMemo(() => {
    return displayDates.map((date) => {
      const d = dayjs(date);
      return d.format("M월 D일");
    });
  }, [displayDates]);
  const orderByDateAndItem = useMemo(() => {
    const result = {};
    displayDates.forEach((date) => {
      result[date] = {};
      selectedItems.forEach((itemId) => {
        result[date][itemId] = 0;
      });
    });
    orderData.forEach((order) => {
      const itemId = order.item_id.toString();
      if (selectedItems.includes(itemId)) {
        const dueDate = dayjs(order.due_date).format("YYYY-MM-DD");
        if (displayDates.includes(dueDate)) {
          result[dueDate][itemId] = (result[dueDate][itemId] || 0) + order.qty;
        }
      }
    });
    return result;
  }, [orderData, displayDates, selectedItems]);
  const generateSummaryData = useCallback(() => {
    if (!itemsData.length || !displayDates.length) return [];
    const selectedItemsData = itemsData.filter((item) =>
      selectedItems.includes(item.id.toString())
    );
    const initialStock = selectedItemsData.reduce((acc, item) => {
      const itemStock = stockData[item.code] || 0;
      return acc + itemStock;
    }, 0);
    const safetyStock = selectedItemsData.reduce((acc, item) => {
      return acc + (item.safetyStock || 0);
    }, 0);
    const dailyProduction = {};
    const dailyStock = {};
    const dailyShipment = {};
    const dailyIncoming = {};
    let totalProduction = 0;
    let totalShipment = 0;
    let totalIncoming = 0;
    displayDates.forEach((date) => {
      dailyProduction[date] = 0;
      dailyShipment[date] = 0;
      dailyIncoming[date] = 0;
      if (date === displayDates[0]) {
        dailyStock[date] = initialStock;
      } else {
        dailyStock[date] = 0;
      }
    });
    selectedItemsData.forEach((item) => {
      Object.entries(item.productionPlan).forEach(([date, qty]) => {
        if (displayDates.includes(date)) {
          dailyProduction[date] += qty;
          totalProduction += qty;
        }
      });
    });
    displayDates.forEach((date) => {
      selectedItems.forEach((itemId) => {
        const shipmentQty = orderByDateAndItem[date]?.[itemId] || 0;
        dailyShipment[date] += shipmentQty;
        totalShipment += shipmentQty;
      });
    });
    if (window.incomingStockData) {
      window.incomingStockData.forEach((stock) => {
        if (stock.item_id && selectedItems.includes(stock.item_id.toString())) {
          const stockDate = dayjs(stock.stock_date).format("YYYY-MM-DD");
          if (displayDates.includes(stockDate)) {
            dailyIncoming[stockDate] =
              (dailyIncoming[stockDate] || 0) + stock.qty;
            totalIncoming += stock.qty;
          }
        }
      });
    }
    let currentStock = initialStock;
    displayDates.forEach((date, index) => {
      if (index > 0) {
        currentStock = currentStock - dailyShipment[date] + dailyIncoming[date];
      } else {
        currentStock = currentStock - dailyShipment[date] + dailyIncoming[date];
      }
      dailyStock[date] = currentStock;
    });
    return [
      {
        id: "summary-initialStock",
        type: "기초재고",
        value: initialStock,
        dates: { [displayDates[0]]: initialStock },
      },
      {
        id: "summary-production",
        type: "생산계획",
        value: totalProduction,
        dates: dailyProduction,
      },
      {
        id: "summary-incoming",
        type: "입고예정",
        value: totalIncoming,
        dates: dailyIncoming,
      },
      {
        id: "summary-shipment",
        type: "출고예정",
        value: totalShipment,
        dates: dailyShipment,
      },
      {
        id: "summary-estimatedStock",
        type: "예상재고",
        value: dailyStock[displayDates[displayDates.length - 1]],
        dates: dailyStock,
      },
      {
        id: "summary-safetyStock",
        type: "안전재고",
        value: safetyStock,
        dates: displayDates.reduce(
          (acc, date) => ({ ...acc, [date]: safetyStock }),
          {}
        ),
      },
    ];
  }, [itemsData, selectedItems, displayDates, stockData, orderByDateAndItem]);
  const generateCalendarEvents = useCallback((summaryData) => {
    if (!summaryData.length) return [];
    const events = [];
    const productionData = summaryData.find((item) => item.type === "생산계획");
    if (productionData) {
      Object.entries(productionData.dates).forEach(([date, qty]) => {
        if (qty > 0) {
          events.push({
            id: `production-${date}`,
            title: `생산: ${qty}`,
            start: date,
            classNames: ["production-event"],
            extendedProps: { type: "production", value: qty },
          });
        }
      });
    }
    const shipmentData = summaryData.find((item) => item.type === "출고예정");
    if (shipmentData) {
      Object.entries(shipmentData.dates).forEach(([date, qty]) => {
        if (qty > 0) {
          events.push({
            id: `output-${date}`,
            title: `출고: ${qty}`,
            start: date,
            classNames: ["output-event"],
            extendedProps: { type: "output", value: qty },
          });
        }
      });
    }
    return events;
  }, []);
  const generateChartOption = useCallback(
    (summaryData, chartDateLabels) => {
      if (!summaryData || !summaryData.length) return {};
      const series = [];
      const productionData = summaryData.find(
        (item) => item.type === "생산계획"
      );
      if (productionData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return productionData.dates[dateStr] || 0;
        });
        series.push({
          name: "생산계획",
          type: "bar",
          stack: "total",
          data: data,
          itemStyle: { color: "#1890ff" },
        });
      }
      const incomingData = summaryData.find((item) => item.type === "입고예정");
      if (incomingData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return incomingData.dates[dateStr] || 0;
        });
        series.push({
          name: "입고예정",
          type: "bar",
          stack: "total",
          data: data,
          itemStyle: { color: "#52c41a" },
        });
      }
      const shipmentData = summaryData.find((item) => item.type === "출고예정");
      if (shipmentData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return shipmentData.dates[dateStr] || 0;
        });
        series.push({
          name: "출고예정",
          type: "bar",
          stack: "total",
          data: data,
          itemStyle: { color: "#fa8c16" },
        });
      }
      const stockData = summaryData.find((item) => item.type === "예상재고");
      if (stockData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return stockData.dates[dateStr] || 0;
        });
        series.push({
          name: "예상재고",
          type: "line",
          data: data,
          symbolSize: 6,
          lineStyle: { width: 3 },
          itemStyle: { color: "#13c2c2" },
        });
      }
      const safetyStockData = summaryData.find(
        (item) => item.type === "안전재고"
      );
      if (safetyStockData) {
        const data = chartDateLabels.map((label, index) => {
          const dateStr = displayDates[index];
          return safetyStockData.dates[dateStr] || 0;
        });
        series.push({
          name: "안전재고",
          type: "line",
          data: data,
          symbolSize: 0,
          lineStyle: { width: 2, type: "dashed" },
          itemStyle: { color: "#f5222d" },
        });
      }
      return {
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          show: false,
        },
        grid: {
          left: "1.58%",
          right: "0%",
          bottom: "10px",
          top: "50px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: true,
          data: chartDateLabels,
          axisLabel: {
            rotate: 0,
            interval: 0,
          },
        },
        yAxis: {
          type: "value",
          name: "수량",
          nameLocation: "end",
        },
        series: series,
      };
    },
    [displayDates]
  );
  const summaryColumns = useMemo(() => {
    const cols = [
      {
        accessorKey: "type",
        header: "항목",
        size: 120,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
      {
        accessorKey: "value",
        header: "합계",
        size: 80,
        Cell: ({ cell }) => cell.getValue() || "",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
      },
    ];
    displayDates.forEach((date) => {
      const formattedDate = dayjs(date).format("MM월 DD일");
      cols.push({
        accessorFn: (row) => row.dates?.[date] ?? "",
        id: date,
        header: formattedDate,
        size: 100,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) => {
          const value = cell.getValue();
          if (value === "") return "";
          if (row.original.type === "예상재고") {
            const safetyStockRow = summaryData.find(
              (item) => item.type === "안전재고"
            );
            const safetyStockValue = safetyStockRow?.dates?.[date] || 0;
            if (value <= safetyStockValue) {
              return (
                <span style={{ color: "red", fontWeight: "bold" }}>
                  {value}
                </span>
              );
            }
          }
          return value;
        },
      });
    });
    return cols;
  }, [displayDates, summaryData]);
  const getFilteredItemsInCategory = useCallback(
    (category) => {
      if (!itemCategories[category]) return [];
      if (!searchTerm) return itemCategories[category];
      return itemCategories[category].filter((itemId) => {
        const item = itemsData.find((data) => data.id.toString() === itemId);
        if (!item) return false;
        const searchLower = searchTerm.toLowerCase();
        return (
          item.id.toString().toLowerCase().includes(searchLower) ||
          item.name.toLowerCase().includes(searchLower)
        );
      });
    },
    [itemCategories, searchTerm, itemsData]
  );
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return Object.keys(itemCategories);
    return Object.keys(itemCategories).filter((category) => {
      if (category.toLowerCase().includes(searchTerm.toLowerCase()))
        return true;
      return getFilteredItemsInCategory(category).length > 0;
    });
  }, [itemCategories, searchTerm, getFilteredItemsInCategory]);
  const calculatedSummaryData = useMemo(
    () => generateSummaryData(),
    [generateSummaryData]
  );
  const currentChartOption = useMemo(
    () => generateChartOption(calculatedSummaryData, chartDateLabels),
    [calculatedSummaryData, chartDateLabels, generateChartOption]
  );
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchResultData();
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [fetchResultData]);
  useEffect(() => {
    if (!loading) {
      setSummaryData(calculatedSummaryData);
    }
  }, [calculatedSummaryData, loading]);
  useEffect(() => {
    if (calculatedSummaryData.length) {
      setCalendarEvents(generateCalendarEvents(calculatedSummaryData));
    }
  }, [calculatedSummaryData, generateCalendarEvents]);
  useEffect(() => {
    if (chartInstance) {
      chartInstance.setOption(currentChartOption, true);
    }
  }, [currentChartOption, chartInstance]);
  useEffect(() => {
    if (!loading) {
      fetchOrderData();
    }
  }, [fetchOrderData, loading, dateRange]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isItemPanelOpen &&
        !isPanelPinned &&
        !event.target.closest(".item-panel") &&
        !event.target.closest(".item-panel-toggle")
      ) {
        setIsItemPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isItemPanelOpen, isPanelPinned]);
  const onChartReady = useCallback((echarts) => setChartInstance(echarts), []);
  const toggleItemPanel = () => {
    if (isDatePanelOpen) setIsDatePanelOpen(false);
    setIsItemPanelOpen(!isItemPanelOpen);
  };
  const toggleDatePanel = () => {
    if (isItemPanelOpen) setIsItemPanelOpen(false);
    setIsDatePanelOpen(!isDatePanelOpen);
  };
  const togglePanelPin = () => {
    setIsPanelPinned(!isPanelPinned);
  };
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) setViewMode(newMode);
  };
  const handleToggleItem = (itemId) => {
    setDataLoading(true);
    setTimeout(() => {
      setSelectedItems((prev) => {
        if (prev.includes(itemId)) {
          return prev.length > 1 ? prev.filter((id) => id !== itemId) : prev;
        }
        return [...prev, itemId];
      });
      setDataLoading(false);
    }, 300);
  };
  const handleSelectAllInCategory = (category, isSelected) => {
    setDataLoading(true);
    setTimeout(() => {
      const categoryItems = itemCategories[category] || [];
      if (isSelected) {
        const newSelected = selectedItems.filter(
          (id) => !categoryItems.includes(id)
        );
        if (newSelected.length > 0) {
          setSelectedItems(newSelected);
        }
      } else {
        const itemsToAdd = categoryItems.filter(
          (id) => !selectedItems.includes(id)
        );
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      }
      setDataLoading(false);
    }, 300);
  };
  const clearAllSelections = () => {
    setDataLoading(true);
    setTimeout(() => {
      const firstCategory = Object.keys(itemCategories)[0];
      if (firstCategory && itemCategories[firstCategory].length > 0) {
        setSelectedItems([itemCategories[firstCategory][0]]);
      } else {
        setSelectedItems([]);
      }
      setDataLoading(false);
    }, 300);
  };
  const clearSearch = () => setSearchTerm("");
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };
  const handlePresetChange = (preset) => {
    setPresetPeriod(preset);
    const today = dayjs();
    let newStart, newEnd;
    switch (preset) {
      case "1week":
        newStart = today;
        newEnd = today.add(7, "day");
        break;
      case "2weeks":
        newStart = today;
        newEnd = today.add(14, "day");
        break;
      case "1month":
        newStart = today.startOf("month");
        newEnd = today.endOf("month");
        break;
      case "3months":
        newStart = today;
        newEnd = today.add(3, "month");
        break;
      default:
        return;
    }
    setDateRange({ start: newStart, end: newEnd });
    if (calendarRef.current && viewMode === "calendar") {
      calendarRef.current.getApi().gotoDate(newStart.toDate());
    }
  };
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  useEffect(() => {
    setTempDateRange(dateRange);
  }, [dateRange]);
  const handleStartDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => {
      if (newDate.isAfter(prev.end)) {
        return { start: newDate, end: newDate };
      }
      return { ...prev, start: newDate };
    });
  };
  const handleEndDateChange = (newDate) => {
    setPresetPeriod("custom");
    setTempDateRange((prev) => {
      if (newDate.isBefore(prev.start)) {
        return { start: newDate, end: newDate };
      }
      return { ...prev, end: newDate };
    });
  };
  const applyDateRange = () => {
    if (tempDateRange.end.isBefore(tempDateRange.start)) {
      alert("종료일은 시작일보다 이후여야 합니다.");
      return;
    }
    setDateRange(tempDateRange);
    setIsDatePanelOpen(false);
    if (calendarRef.current && viewMode === "calendar") {
      setTimeout(() => {
        if (calendarRef.current) {
          calendarRef.current.getApi().gotoDate(tempDateRange.start.toDate());
        }
      }, 0);
    }
  };
  const handleCalendarDateSet = (dateInfo) => {};
  const handleEventClick = (clickInfo) => {
    const { type, value } = clickInfo.event.extendedProps;
    const dateStr = dayjs(clickInfo.event.startStr).format("YYYY년 MM월 DD일");
    let typeText = "";
    switch (type) {
      case "production":
        typeText = "생산계획";
        break;
      case "input":
        typeText = "입고예정";
        break;
      case "output":
        typeText = "출고예정";
        break;
      default:
        typeText = "일정";
    }
    alert(`${dateStr} ${typeText}: ${value}`);
  };
  const getItemName = useCallback(
    (itemId) => {
      const item = itemsData.find((item) => item.id.toString() === itemId);
      return item ? item.name : itemId;
    },
    [itemsData]
  );
  return (
    <S.Container>
      <ItemSelectionPanel
        isOpen={isItemPanelOpen}
        isPinned={isPanelPinned}
        onClose={() => setIsItemPanelOpen(false)}
        onTogglePin={togglePanelPin}
        itemCategories={itemCategories}
        selectedItems={selectedItems}
        expandedCategories={expandedCategories}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onClearSearch={clearSearch}
        onToggleCategory={toggleCategory}
        onToggleItem={handleToggleItem}
        onSelectAllInCategory={handleSelectAllInCategory}
        onClearAllSelections={clearAllSelections}
        getItemName={getItemName}
        filteredCategories={filteredCategories}
        getFilteredItemsInCategory={getFilteredItemsInCategory}
      />
      <DateSelectionPanel
        isOpen={isDatePanelOpen}
        isPinned={isPanelPinned}
        onClose={() => setIsDatePanelOpen(false)}
        onTogglePin={togglePanelPin}
        presetPeriod={presetPeriod}
        onPresetChange={handlePresetChange}
        tempDateRange={tempDateRange}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onApply={applyDateRange}
      />
      <S.MainContent ref={mainContentRef}>
        <Toolbar
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onToggleItemPanel={toggleItemPanel}
          onToggleDatePanel={toggleDatePanel}
          selectedItemsCount={selectedItems.length}
          dateRange={dateRange}
        />
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <S.ContentWrapper>
            {viewMode === "table" ? (
              <TableView
                summaryData={summaryData}
                summaryColumns={summaryColumns}
                chartDateLabels={chartDateLabels}
                currentChartOption={currentChartOption}
                onChartReady={onChartReady}
                dataLoading={dataLoading}
                selectedItems={selectedItems}
                itemsData={itemsData}
                displayDates={displayDates}
              />
            ) : (
              <CalendarView
                calendarRef={calendarRef}
                calendarEvents={calendarEvents}
                onDateSet={handleCalendarDateSet}
                onEventClick={handleEventClick}
                dateRange={dateRange}
              />
            )}
          </S.ContentWrapper>
        )}
      </S.MainContent>
    </S.Container>
  );
}

export default ResultGrid;
